// Astrea Ops - Event Form PDF export (v7.66, 9 Oct 2026).
//
// Builds a PDF of the first three Event Form pages (Event Info, Site Visit / Venue, Event
// Planning) entirely in the browser, with no library: Helvetica text (the standard PDF fonts,
// so nothing is embedded), the Astrea logo as a JPEG, US Letter pages, a header, and a footer
// with page numbers. App.jsx turns the form into a plain description (sections, groups, rows)
// and this file lays it out.
//
// Martin, 9 Oct 2026: every field is shown, blank ones left empty; a value the AI Form
// Assistant filled (or the venue split filled) that nobody has reviewed yet is printed with
// *** NEEDS REVIEW *** under it.
//
// Text is written in WinAnsi (Windows-1252). House style is applied on the way in: curly quotes
// become straight quotes, en and em dashes become short hyphens, the ellipsis character becomes
// three dots, and non-breaking or invisible spaces are removed. Anything WinAnsi cannot show
// prints as "?".

// Glyph widths (per 1000 units) for character codes 32-255, Helvetica and Helvetica-Bold.
const W_REG = [278,278,355,556,556,889,667,191,333,333,389,584,278,333,278,278,556,556,556,556,556,556,556,556,556,556,278,278,584,584,584,556,1015,667,667,722,722,667,611,778,722,278,500,667,556,833,722,778,667,778,722,667,611,722,667,944,667,667,611,278,278,278,469,556,333,556,556,500,556,556,278,556,556,222,222,500,222,833,556,556,556,556,333,500,278,556,500,722,500,500,500,334,260,334,584,761,556,0,222,556,333,1000,556,556,333,1000,667,333,1000,0,611,0,0,222,222,333,333,350,556,1000,333,1000,500,333,944,0,500,667,278,333,556,556,556,556,260,556,333,737,370,556,584,333,737,333,400,584,333,333,333,556,537,278,333,333,365,556,834,834,834,611,667,667,667,667,667,667,1000,722,667,667,667,667,278,278,278,278,722,722,778,778,778,778,778,584,778,722,722,722,722,667,667,611,556,556,556,556,556,556,889,500,556,556,556,556,278,278,278,278,556,556,556,556,556,556,556,584,611,556,556,556,556,500,556,500];
const W_BOLD = [278,333,474,556,556,889,722,238,333,333,389,584,278,333,278,278,556,556,556,556,556,556,556,556,556,556,333,333,584,584,584,611,975,722,722,722,722,667,611,778,722,278,556,722,611,833,722,778,667,778,722,667,611,722,667,944,667,667,611,333,278,333,584,556,333,556,611,556,611,556,333,611,611,278,278,556,278,889,611,611,611,611,389,556,333,611,556,778,556,556,500,389,280,389,584,761,556,0,278,556,500,1000,556,556,333,1000,667,333,1000,0,611,0,0,278,278,500,500,350,556,1000,333,1000,556,333,944,0,500,667,278,333,556,556,556,556,280,556,333,737,370,556,584,333,737,333,400,584,333,333,333,611,556,278,333,333,365,556,834,834,834,611,722,722,722,722,722,722,1000,722,667,667,667,667,278,278,278,278,722,722,778,778,778,778,778,584,778,722,722,722,722,667,667,611,556,556,556,556,556,556,889,556,556,556,556,556,278,278,278,278,611,611,611,611,611,611,611,584,611,611,611,611,611,556,611,556];

// Unicode characters WinAnsi keeps in its 128-159 block (house-style replacements come first).
const CP1252 = { 0x20AC: 0x80, 0x201A: 0x82, 0x0192: 0x83, 0x201E: 0x84, 0x2020: 0x86, 0x2021: 0x87, 0x02C6: 0x88, 0x2030: 0x89, 0x0160: 0x8A, 0x2039: 0x8B, 0x0152: 0x8C, 0x017D: 0x8E, 0x02DC: 0x98, 0x2122: 0x99, 0x0161: 0x9A, 0x203A: 0x9B, 0x0153: 0x9C, 0x017E: 0x9E, 0x0178: 0x9F };

// Text -> a string of WinAnsi codes (each char code 0-255), house style applied.
export function pdfText(s) {
  const t = String(s ?? '')
    .replace(/\r\n?/g, '\n')
    .replace(/[\u2018\u2019\u201B\u2032]/g, "'")
    .replace(/[\u201C\u201D\u201F\u2033]/g, '"')
    .replace(/[\u2010-\u2015\u2212]/g, '-')
    .replace(/\u2026/g, '...')
    .replace(/[\u2022\u25CF\u25AA\u2023\u2043]/g, '-')
    .replace(/[\u00A0\u2000-\u200A\u202F\u205F\u3000\t]/g, ' ')
    .replace(/[\u00AD\u200B-\u200D\u2060\uFEFF]/g, '')
    .normalize('NFC');
  let out = '';
  for (const ch of t) {
    const c = ch.codePointAt(0);
    if (c === 10) out += '\n';
    else if (c >= 32 && c <= 126) out += ch;
    else if (c >= 160 && c <= 255) out += String.fromCharCode(c);
    else if (CP1252[c]) out += String.fromCharCode(CP1252[c]);
    else if (c >= 32) out += '?';
  }
  return out;
}

export const widthOf = (s, bold, size) => {
  const tbl = bold ? W_BOLD : W_REG;
  let w = 0;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    w += c >= 32 && c <= 255 ? (tbl[c - 32] || 556) : 556;
  }
  return (w * size) / 1000;
};

// Greedy word wrap of WinAnsi text; respects line breaks; breaks words longer than a line.
export function wrapText(s, bold, size, maxW) {
  const lines = [];
  String(s || '').split('\n').forEach((para) => {
    const words = para.split(/ +/);
    let cur = '';
    const push = (x) => lines.push(x.replace(/\s+$/, ''));
    for (let w of words) {
      const cand = cur ? cur + ' ' + w : w;
      if (widthOf(cand, bold, size) <= maxW) { cur = cand; continue; }
      if (cur) push(cur);
      cur = '';
      while (widthOf(w, bold, size) > maxW) {
        let n = w.length;
        while (n > 1 && widthOf(w.slice(0, n), bold, size) > maxW) n--;
        push(w.slice(0, n));
        w = w.slice(n);
      }
      cur = w;
    }
    push(cur);
  });
  // drop trailing empty lines
  while (lines.length > 1 && lines[lines.length - 1] === '') lines.pop();
  return lines;
}

const esc = (s) => {
  let o = '';
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    if (c === 40 || c === 41 || c === 92) o += '\\' + s[i];
    else if (c < 32 || c > 126) o += '\\' + c.toString(8).padStart(3, '0');
    else o += s[i];
  }
  return o;
};
const num = (n) => (Math.round(n * 100) / 100).toString();
const rgb = (hex) => {
  const h = String(hex || '#000000').replace('#', '');
  const v = h.length === 3 ? h.split('').map((x) => x + x).join('') : h.padEnd(6, '0').slice(0, 6);
  return [0, 2, 4].map((i) => num(parseInt(v.slice(i, i + 2), 16) / 255)).join(' ');
};

// Width, height and colour components of a baseline or progressive JPEG.
export function jpegInfo(b) {
  if (!b || b[0] !== 0xff || b[1] !== 0xd8) return null;
  let i = 2;
  while (i + 9 < b.length) {
    if (b[i] !== 0xff) { i++; continue; }
    const m = b[i + 1];
    const len = (b[i + 2] << 8) | b[i + 3];
    if ((m >= 0xc0 && m <= 0xc3) || (m >= 0xc5 && m <= 0xc7) || (m >= 0xc9 && m <= 0xcb) || (m >= 0xcd && m <= 0xcf)) {
      return { height: (b[i + 5] << 8) | b[i + 6], width: (b[i + 7] << 8) | b[i + 8], components: b[i + 9] };
    }
    i += 2 + len;
  }
  return null;
}

const INK = '#0d1c2e';
const LABEL = '#4d6a82';
const RULE = '#a8bece';
const BAND = '#e8f0f8';
const FLAG = '#c03838';
const FLAG_TEXT = '*** NEEDS REVIEW ***';

// doc: {title, eventName, metaLines:[..], notice, generatedLine, footerLeft, logo:{bytes}|null,
//       sections:[{title, groups:[{title, color, rows:[row]}]}]}
// row: {type:"fields", cells:[{label, value, flag}]} | {type:"subhead", text}
//    | {type:"table", columns:[{label, width}], rows:[[{value, flag}]], empty}
export function buildEventFormPdf(doc) {
  const PW = 612, PH = 792, ML = 40, MR = 40, MT = 38, MB = 48;
  const CW = PW - ML - MR;
  const pages = [];
  let ops = null;
  let y = 0; // distance from the top of the page
  const Y = (top) => PH - top;

  const logo = doc.logo && doc.logo.bytes ? { bytes: doc.logo.bytes, info: jpegInfo(doc.logo.bytes) } : null;
  const hasLogo = !!(logo && logo.info);

  const text = (x, top, s, { bold = false, size = 9, color = INK } = {}) => {
    ops.push('BT /' + (bold ? 'F2' : 'F1') + ' ' + num(size) + ' Tf ' + rgb(color) + ' rg ' + num(x) + ' ' + num(Y(top)) + ' Td (' + esc(s) + ') Tj ET');
  };
  const textRight = (xr, top, s, o = {}) => text(xr - widthOf(s, !!o.bold, o.size || 9), top, s, o);
  const rect = (x, top, w, h, { fill = null, stroke = null, lw = 0.5 } = {}) => {
    if (!fill && !stroke) return;
    let o = '';
    if (fill) o += rgb(fill) + ' rg ';
    if (stroke) o += rgb(stroke) + ' RG ' + num(lw) + ' w ';
    o += num(x) + ' ' + num(Y(top + h)) + ' ' + num(w) + ' ' + num(h) + ' re ' + (fill && stroke ? 'B' : fill ? 'f' : 'S');
    ops.push(o);
  };
  const hline = (x1, x2, top, color = RULE, lw = 0.5) => ops.push(rgb(color) + ' RG ' + num(lw) + ' w ' + num(x1) + ' ' + num(Y(top)) + ' m ' + num(x2) + ' ' + num(Y(top)) + ' l S');

  const titleS = pdfText(doc.title || 'Event Planning Form');
  const eventS = pdfText(doc.eventName || '');
  const newPage = () => {
    ops = [];
    pages.push(ops);
    if (pages.length === 1) {
      // Full header on page 1: logo, title, event, details
      const lh = 34;
      if (hasLogo) ops.push('q ' + num(lh * logo.info.width / logo.info.height) + ' 0 0 ' + lh + ' ' + num(ML) + ' ' + num(Y(MT + lh)) + ' cm /Im1 Do Q');
      textRight(PW - MR, MT + 13, titleS.toUpperCase(), { bold: true, size: 14, color: INK });
      const ev = wrapText(eventS, false, 10.5, CW - 150);
      ev.slice(0, 2).forEach((l, i) => textRight(PW - MR, MT + 28 + i * 12.5, l, { size: 10.5, color: '#263648' }));
      y = MT + Math.max(lh, 28 + Math.min(ev.length, 2) * 12.5) + 8;
      (doc.metaLines || []).forEach((m) => {
        wrapText(pdfText(m), false, 8.5, CW).forEach((l) => { text(ML, y + 8, l, { size: 8.5, color: '#435f78' }); y += 11; });
      });
      if (doc.notice) {
        y += 2;
        wrapText(pdfText(doc.notice), true, 8, CW).forEach((l) => { text(ML, y + 8, l, { bold: true, size: 8, color: FLAG }); y += 10.5; });
      }
      y += 4;
      hline(ML, PW - MR, y, INK, 1);
      y += 10;
    } else {
      const s = titleS + (eventS ? ' - ' + eventS : '');
      const l = wrapText(s, false, 8, CW)[0] || '';
      text(ML, MT + 7, l, { size: 8, color: LABEL });
      hline(ML, PW - MR, MT + 12, RULE, 0.5);
      y = MT + 22;
    }
  };
  const bottom = PH - MB;
  const room = () => bottom - y;

  // -- sections --
  const PAD = 3.2, LBL = 6.8, LBL_LH = 8.2, VAL = 9, VAL_LH = 11, GAP = 6, FLAG_LH = 10;
  const cellParts = (cell, w) => {
    const label = wrapText(pdfText(String(cell.label || '').toUpperCase()), true, LBL, w - 2 * PAD);
    const v = pdfText(String(cell.value ?? '').trim());
    const value = v ? wrapText(v, false, VAL, w - 2 * PAD) : [''];
    return { label, value, flag: !!cell.flag && !!v };
  };

  const drawFieldsRow = (row) => {
    const n = row.cells.length;
    const w = (CW - GAP * (n - 1)) / n;
    const parts = row.cells.map((c) => cellParts(c, w));
    const labH = Math.max(...parts.map((p) => p.label.length)) * LBL_LH;
    const valLines = (p) => p.value.length;
    let start = parts.map(() => 0); // next value line to draw per cell
    let first = true;
    for (;;) {
      const remaining = parts.map((p, i) => valLines(p) - start[i]);
      const flagH = parts.some((p, i) => p.flag && remaining[i] >= 0) ? FLAG_LH : 0;
      const needLines = Math.max(...remaining, first ? 1 : 0);
      const fullH = PAD * 2 + labH + needLines * VAL_LH + flagH;
      let fit = needLines;
      if (fullH > room()) {
        // Fit what we can if at least three lines go on this page; otherwise start a new page.
        const canLines = Math.floor((room() - PAD * 2 - labH - flagH) / VAL_LH);
        const pageLines = Math.floor((bottom - (MT + 22) - PAD * 2 - labH - flagH) / VAL_LH);
        if (fullH <= bottom - (MT + 22) || canLines < 3) {
          newPage();
          if (fullH <= room()) fit = needLines;
          else fit = pageLines;
        } else fit = canLines;
      }
      fit = Math.max(1, fit);
      const done = parts.map((p, i) => start[i] + fit >= valLines(p));
      const showFlag = parts.some((p, i) => p.flag && done[i]);
      const h = PAD * 2 + labH + Math.max(1, Math.min(fit, Math.max(...remaining))) * VAL_LH + (showFlag ? FLAG_LH : 0);
      parts.forEach((p, i) => {
        const x = ML + i * (w + GAP);
        rect(x, y, w, h, { fill: '#ffffff', stroke: RULE, lw: 0.5 });
        p.label.forEach((l, j) => text(x + PAD, y + PAD + 5.6 + j * LBL_LH, (j === p.label.length - 1 && !first ? l + ' (CONTINUED)' : l), { bold: true, size: LBL, color: LABEL }));
        const lines = p.value.slice(start[i], start[i] + fit);
        lines.forEach((l, j) => { if (l) text(x + PAD, y + PAD + labH + 8 + j * VAL_LH, l, { size: VAL, color: INK }); });
        if (p.flag && done[i]) text(x + PAD, y + PAD + labH + 8 + Math.max(lines.length, 1) * VAL_LH - 1, FLAG_TEXT, { bold: true, size: 7.5, color: FLAG });
      });
      y += h + 3;
      start = start.map((s) => s + fit);
      first = false;
      if (parts.every((p, i) => start[i] >= valLines(p))) break;
    }
  };

  const drawTable = (row) => {
    const cols = row.columns;
    const totalW = cols.reduce((a, c) => a + (c.width || 1), 0);
    const ws = cols.map((c) => (CW * (c.width || 1)) / totalW);
    const xs = ws.map((_, i) => ML + ws.slice(0, i).reduce((a, b) => a + b, 0));
    const head = () => {
      const hl = cols.map((c, i) => wrapText(pdfText(String(c.label).toUpperCase()), true, LBL, ws[i] - 2 * PAD));
      const h = PAD * 2 + Math.max(...hl.map((l) => l.length)) * LBL_LH;
      cols.forEach((c, i) => {
        rect(xs[i], y, ws[i], h, { fill: BAND, stroke: RULE });
        hl[i].forEach((l, j) => text(xs[i] + PAD, y + PAD + 5.6 + j * LBL_LH, l, { bold: true, size: LBL, color: LABEL }));
      });
      y += h;
    };
    const data = row.rows && row.rows.length ? row.rows : null;
    if (room() < 40) newPage();
    head();
    if (!data) {
      const h = PAD * 2 + VAL_LH;
      rect(ML, y, CW, h, { fill: '#ffffff', stroke: RULE });
      text(ML + PAD, y + PAD + 8, pdfText(row.empty || 'None recorded'), { size: VAL, color: LABEL });
      y += h + 6;
      return;
    }
    data.forEach((r) => {
      const cl = r.map((c, i) => {
        const v = pdfText(String((c && c.value) ?? '').trim());
        return { lines: v ? wrapText(v, false, VAL, ws[i] - 2 * PAD) : [''], flag: !!(c && c.flag) && !!v };
      });
      const lines = Math.max(...cl.map((c) => c.lines.length + (c.flag ? 1 : 0)));
      const h = PAD * 2 + lines * VAL_LH;
      if (h > room()) { newPage(); head(); }
      cl.forEach((c, i) => {
        rect(xs[i], y, ws[i], h, { fill: '#ffffff', stroke: RULE });
        c.lines.forEach((l, j) => { if (l) text(xs[i] + PAD, y + PAD + 8 + j * VAL_LH, l, { size: VAL, color: INK }); });
        if (c.flag) text(xs[i] + PAD, y + PAD + 8 + c.lines.length * VAL_LH - 1, FLAG_TEXT, { bold: true, size: 7.5, color: FLAG });
      });
      y += h;
    });
    y += 6;
  };

  const rowMinH = (row) => {
    if (row.type === 'subhead') return 16 + 30;
    if (row.type === 'table') return 46;
    return PAD * 2 + LBL_LH + VAL_LH + 4;
  };

  (doc.sections || []).forEach((sec, si) => {
    if (si === 0) { if (!ops) newPage(); } else newPage();
    // Section band
    const st = pdfText(String(sec.title || '').toUpperCase());
    rect(ML, y, CW, 19, { fill: INK });
    text(ML + 8, y + 13, st, { bold: true, size: 11, color: '#ffffff' });
    y += 25;
    (sec.groups || []).forEach((g) => {
      const first = (g.rows || [])[0];
      if (room() < 20 + (first ? rowMinH(first) : 0)) newPage();
      rect(ML, y, CW, 15, { fill: BAND });
      rect(ML, y, 3, 15, { fill: g.color || INK });
      text(ML + 9, y + 10.5, pdfText(String(g.title || '').toUpperCase()), { bold: true, size: 8.5, color: g.color || INK });
      y += 20;
      (g.rows || []).forEach((row) => {
        if (row.type === 'subhead') {
          if (room() < 46) newPage();
          text(ML, y + 8, pdfText(String(row.text || '').toUpperCase()), { bold: true, size: 8, color: '#2878c8' });
          y += 13;
        } else if (row.type === 'table') drawTable(row);
        else if (row.cells && row.cells.length) drawFieldsRow(row);
      });
      y += 2;
    });
  });
  if (!pages.length) newPage();

  // Footers, now the page count is known
  const total = pages.length;
  const footL = pdfText(doc.footerLeft || '');
  const gen = pdfText(doc.generatedLine || '');
  pages.forEach((p, i) => {
    ops = p;
    hline(ML, PW - MR, PH - MB + 14, RULE, 0.5);
    if (footL) text(ML, PH - MB + 25, footL, { size: 7.5, color: LABEL });
    if (gen) text(ML, PH - MB + 35, gen, { size: 7, color: LABEL });
    textRight(PW - MR, PH - MB + 25, 'Page ' + (i + 1) + ' of ' + total, { bold: true, size: 7.5, color: LABEL });
  });

  // -- assemble the file --
  const enc = (s) => { const u = new Uint8Array(s.length); for (let i = 0; i < s.length; i++) u[i] = s.charCodeAt(i) & 255; return u; };
  const chunks = [];
  let len = 0;
  const offsets = [];
  const add = (u) => { chunks.push(u); len += u.length; };
  const obj = (n, body) => { offsets[n] = len; add(enc(n + ' 0 obj\n')); (Array.isArray(body) ? body : [body]).forEach((b) => add(typeof b === 'string' ? enc(b) : b)); add(enc('\nendobj\n')); };
  add(enc('%PDF-1.4\n%\xe2\xe3\xcf\xd3\n'));
  const nPages = pages.length;
  const firstPageObj = hasLogo ? 7 : 6;
  const kids = pages.map((_, i) => (firstPageObj + i * 2) + ' 0 R').join(' ');
  obj(1, '<< /Type /Catalog /Pages 2 0 R >>');
  obj(2, '<< /Type /Pages /Kids [' + kids + '] /Count ' + nPages + ' >>');
  obj(3, '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>');
  obj(4, '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>');
  const d = new Date();
  const pd = (n) => String(n).padStart(2, '0');
  const stamp = 'D:' + d.getUTCFullYear() + pd(d.getUTCMonth() + 1) + pd(d.getUTCDate()) + pd(d.getUTCHours()) + pd(d.getUTCMinutes()) + pd(d.getUTCSeconds()) + 'Z';
  obj(5, '<< /Title (' + esc(titleS + (eventS ? ' - ' + eventS : '')) + ') /Author (Astrea Risk Management Services) /Producer (Astrea Ops) /CreationDate (' + stamp + ') >>');
  if (hasLogo) {
    const cs = logo.info.components === 1 ? '/DeviceGray' : logo.info.components === 4 ? '/DeviceCMYK' : '/DeviceRGB';
    obj(6, ['<< /Type /XObject /Subtype /Image /Width ' + logo.info.width + ' /Height ' + logo.info.height + ' /ColorSpace ' + cs + ' /BitsPerComponent 8 /Filter /DCTDecode' + (logo.info.components === 4 ? ' /Decode [1 0 1 0 1 0 1 0]' : '') + ' /Length ' + logo.bytes.length + ' >>\nstream\n', logo.bytes, '\nendstream']);
  }
  const res = '<< /Font << /F1 3 0 R /F2 4 0 R >>' + (hasLogo ? ' /XObject << /Im1 6 0 R >>' : '') + ' >>';
  pages.forEach((p, i) => {
    const pn = firstPageObj + i * 2;
    const content = p.join('\n');
    obj(pn, '<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ' + PW + ' ' + PH + '] /Resources ' + res + ' /Contents ' + (pn + 1) + ' 0 R >>');
    obj(pn + 1, '<< /Length ' + content.length + ' >>\nstream\n' + content + '\nendstream');
  });
  const nObj = firstPageObj + nPages * 2;
  const xref = len;
  let x = 'xref\n0 ' + nObj + '\n0000000000 65535 f \n';
  for (let i = 1; i < nObj; i++) x += String(offsets[i]).padStart(10, '0') + ' 00000 n \n';
  x += 'trailer\n<< /Size ' + nObj + ' /Root 1 0 R /Info 5 0 R >>\nstartxref\n' + xref + '\n%%EOF\n';
  add(enc(x));
  const out = new Uint8Array(len);
  let o = 0;
  chunks.forEach((c) => { out.set(c, o); o += c.length; });
  return out;
}
